import React from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendLineChart } from "@/components/Charts";
import { SummaryBanner, SectionBox, EmptyBlock } from "@/components/DashboardBits";
import { recomputeForecast } from "@/lib/api";
import { toast } from "sonner";

const METRICS = [
  { key: "revenue", label: "Revenue" },
  { key: "denials", label: "Claim Denials" },
  { key: "bed_occupancy", label: "Bed Occupancy" },
  { key: "procurement", label: "Procurement Spend" },
  { key: "staff_hours", label: "Staffing Demand" },
];

export default function Forecasting({ sessionId, forecast, setForecast }) {
  const [method, setMethod] = React.useState("linear");

  const changeMethod = async (m) => {
    setMethod(m);
    try {
      const res = await recomputeForecast(sessionId, m, 3);
      setForecast(res.forecast);
    } catch {
      toast.error("Could not update forecast");
    }
  };

  const availableMetrics = METRICS.filter(
    (mk) =>
      forecast?.history?.some((h) => h[mk.key] !== undefined && h[mk.key] !== null) ||
      forecast?.metrics?.includes(mk.key)
  );

  return (
    <div className="space-y-8">
      <SummaryBanner
        title="Next Quarter At a Glance"
        text="Simple trend projections across five operational levers. Use linear regression for long-run direction or moving average for a smoothed near-term view."
        testId="forecast-summary-banner"
      />

      <div className="flex items-center justify-between flex-wrap gap-4 border border-gray-200 p-5 bg-gray-50/60">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-gray-500 font-bold">
            / Forecast method
          </div>
          <div className="font-display text-lg font-bold tracking-tight mt-1">
            Switch between projection models
          </div>
        </div>
        <div className="flex items-center gap-3" data-testid="forecast-toggle">
          <Select value={method} onValueChange={changeMethod}>
            <SelectTrigger className="w-[200px] h-10 rounded-none border-gray-300 text-sm" data-testid="forecast-method-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="linear" data-testid="opt-linear">Linear regression</SelectItem>
              <SelectItem value="moving_avg" data-testid="opt-moving">Moving average (3-period)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {!forecast?.history?.length ? (
        <EmptyBlock text="No date column detected to generate forecasts." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {availableMetrics.map((m) => (
            <SectionBox
              key={m.key}
              title={`${m.label} — 3-month forecast`}
              eyebrow="/ Projection"
              testId={`forecast-${m.key}`}
            >
              <TrendLineChart
                history={forecast.history}
                forecast={forecast.forecast}
                metric={m.key}
                height={260}
              />
            </SectionBox>
          ))}
        </div>
      )}
    </div>
  );
}
